import { useEffect, useRef, useState } from 'react'

/**
 * O número SUBINDO até o valor novo, em vez de saltar — pedido em 13/10 para o
 * total e para o vendido de cada marca.
 *
 * Quatro decisões que o telão impõe:
 *
 *  1. **A duração cresce com o salto, e tem teto.** Entrou R$ 2 mil: 600 ms, o
 *     bastante para o olho pegar o movimento. Entrou R$ 800 mil: 2,2 s, que é
 *     onde a conta para. Foi o pedido — "a velocidade aumenta conforme a
 *     quantidade de dinheiro, mas sem a pessoa ficar um minuto olhando". A curva
 *     é pela RAIZ do salto, não linear: linear daria 40 ms num pedido pequeno
 *     (invisível) e meio minuto num grande.
 *  2. **`requestAnimationFrame`, e ele PARA no fim.** Nada de laço permanente:
 *     o telão fica dias ligado, e aqui a animação roda ~2 s a cada ciclo de
 *     2 min. Fora disso não há quadro nenhum sendo pedido.
 *  3. **Só sobe a partir de um valor que existia.** A primeira medição aparece
 *     direto, sem subir de zero: contar de R$ 0 a R$ 6 mi no carregamento
 *     mostraria números falsos por dois segundos, e alguém fotografa o telão.
 *     Queda (devolução) também vai direto — "dinheiro descendo" animado seria
 *     ruído, e o número certo importa mais que o efeito.
 *  4. **`prefers-reduced-motion` desliga.** Quem pediu menos movimento recebe o
 *     número final na hora.
 */

/** Onde a duração começa e onde ela para, em ms. */
const DURACAO_MINIMA = 600
const DURACAO_MAXIMA = 2200
/** O salto que já merece a duração máxima. Acima disto não acelera mais. */
const SALTO_CHEIO = 800_000

function duracaoDoSalto(salto: number): number {
  const proporcao = Math.min(Math.sqrt(salto) / Math.sqrt(SALTO_CHEIO), 1)
  return DURACAO_MINIMA + (DURACAO_MAXIMA - DURACAO_MINIMA) * proporcao
}

/** Desaceleração no fim: o número chega ao alvo sem parar de supetão. */
function suavizar(t: number): number {
  return 1 - (1 - t) ** 3
}

export function usarNumeroAnimado(alvo: number | null): number | null {
  const [exibido, setExibido] = useState<number | null>(alvo)
  const quadro = useRef<number | null>(null)
  const partida = useRef<number | null>(alvo)

  useEffect(() => {
    const de = partida.current
    partida.current = alvo

    // Sem valor, sem animação: `null` é "ninguém mediu" e a tela mostra `—`.
    if (alvo === null || de === null || de === alvo) {
      setExibido(alvo)
      return
    }
    // Queda vai direto, e a primeira medição também (tratada acima por `de`).
    if (alvo < de) {
      setExibido(alvo)
      return
    }
    const reduzir =
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduzir) {
      setExibido(alvo)
      return
    }

    const duracao = duracaoDoSalto(alvo - de)
    const inicio = performance.now()

    const passo = (agora: number) => {
      const t = Math.min((agora - inicio) / duracao, 1)
      setExibido(de + (alvo - de) * suavizar(t))
      if (t < 1) {
        quadro.current = requestAnimationFrame(passo)
        return
      }
      /* Fecha no ALVO exato: interpolação em ponto flutuante para em
         6.013.999,97, e o telão mostraria um milhar a menos do que o painel
         mediu — num número que vai ser fotografado. */
      quadro.current = null
      setExibido(alvo)
    }
    quadro.current = requestAnimationFrame(passo)

    return () => {
      if (quadro.current !== null) cancelAnimationFrame(quadro.current)
      quadro.current = null
    }
  }, [alvo])

  return exibido
}
